import classNames from 'classnames';
import { Form, Formik, type FormikConfig } from 'formik';
import { memo } from 'react';
import { object, string } from 'yup';

import { FormikField } from '@air/formik-field';
import { Button } from '@air/primitive-button';
import { type Color } from '@air/primitive-color-picker';
import { Input } from '@air/primitive-input';
import { Label } from '@air/primitive-label';
import { Textarea } from '@air/primitive-textarea';

import { GeneralLibraryInfo } from '~/components/LibraryBeta/GeneralLibrarySettingsModal/components/GeneralLibraryInfo';
import { UpdateLibraryIcon } from '~/components/LibraryBeta/LibrarySettingsModal/components/UpdateLibraryIcon';

export const GeneralLibraryUpdateDetailsValidationSchema = object().shape({
  title: string()
    .max(255, 'Library name must be less than 255 characters.')
    .matches(/^[^:|,]*$/, 'Characters (:, |) not allowed')
    .trim()
    .required('Library name is required.'),
  description: string(),
});

export type GeneralLibraryDetailsValues = {
  color?: Color;
  description?: string;
  icon?: string;
  title: string;
};

export type GeneralLibraryUpdateDetails = Pick<
  FormikConfig<GeneralLibraryDetailsValues>,
  'initialValues' | 'onSubmit'
> & {
  isSubmitting?: boolean;
  isDisabled?: boolean;
  workspaceName: string;
  colors?: Color[];
};

export const GeneralLibraryUpdateDetails = memo(
  ({ initialValues, isSubmitting, isDisabled, onSubmit, workspaceName, colors }: GeneralLibraryUpdateDetails) => {
    return (
      <Formik<GeneralLibraryDetailsValues>
        enableReinitialize
        initialValues={initialValues}
        onSubmit={onSubmit}
        validationSchema={GeneralLibraryUpdateDetailsValidationSchema}
      >
        {({ dirty, isValid }) => (
          <Form className="flex flex-col gap-3">
            <div className="flex flex-col gap-4">
              <div className="flex flex-col gap-1">
                <Label htmlFor="library-title" size="small">
                  Name
                </Label>
                <div className="flex items-center gap-2">
                  <div className={classNames('shrink-0', isDisabled && 'pointer-events-none opacity-50')}>
                    {/* Uses same component as UpdateLibraryDetails */}
                    <UpdateLibraryIcon className="shrink-0" colors={colors} />
                  </div>
                  <FormikField
                    isLabelHidden={true}
                    className="w-full"
                    component={<Input />}
                    id="library-title"
                    label="Title"
                    name="title"
                  />
                </div>
              </div>

              <FormikField
                id="library-description"
                label="Description"
                name="description"
                component={<Textarea placeholder="What is this library used for" />}
              />

              <GeneralLibraryInfo workspaceName={workspaceName} />
            </div>
            <div className="-mx-8 mt-2 -mb-6 flex items-center justify-end border-t border-t-grey-5 px-8 py-4">
              <Button
                appearance="filled"
                color="blue"
                disabled={!dirty || isDisabled || isSubmitting || !isValid}
                isLoading={isSubmitting}
                size="large"
                type="submit"
              >
                Save changes
              </Button>
            </div>
          </Form>
        )}
      </Formik>
    );
  },
);

GeneralLibraryUpdateDetails.displayName = 'EditableGeneralLibraryDetails';
